import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, IMG } from "@/lib/api";
import SEO from "@/components/SEO";
import { fireEvent } from "@/lib/ads";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Sparkles, Send, BadgeCheck, Check, MapPin, BellRing, RotateCcw, Loader2 } from "lucide-react";

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const formatPrice = (n, isRent) => {
  if (!n) return "";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${n.toLocaleString("en-IN")}${isRent ? "/mo" : ""}`;
};

const FURN = { fully: "Fully furnished", semi: "Semi-furnished", semi_furnished: "Semi-furnished", unfurnished: "Unfurnished" };

// Truthful positive highlights — built ONLY from real listing fields.
function highlightsFor(p) {
  const h = [];
  if (p.owner_verified) h.push("Verified owner");
  h.push("Zero brokerage");
  if (p.area_sqft) h.push(`${p.area_sqft} sqft`);
  if (p.furnishing && FURN[p.furnishing]) h.push(FURN[p.furnishing]);
  if (p.is_featured) h.push("Featured");
  return h.slice(0, 4);
}

const RENT_BUDGETS = [
  { label: "Under ₹15k", max_price: 15000 },
  { label: "₹15k–25k", min_price: 15000, max_price: 25000 },
  { label: "₹25k–40k", min_price: 25000, max_price: 40000 },
  { label: "₹40k+", min_price: 40000 },
  { label: "Any budget", any: true },
];
const BUY_BUDGETS = [
  { label: "Under ₹50L", max_price: 5000000 },
  { label: "₹50L–1Cr", min_price: 5000000, max_price: 10000000 },
  { label: "₹1–2Cr", min_price: 10000000, max_price: 20000000 },
  { label: "₹2Cr+", min_price: 20000000 },
  { label: "Any budget", any: true },
];

// Compact card used inside the chat.
function ChatListing({ p }) {
  const isRent = p.listing_type === "rent";
  const img = p.image_paths?.[0]
    ? IMG(p.image_paths[0])
    : "https://images.pexels.com/photos/16110999/pexels-photo-16110999.jpeg?auto=compress&cs=tinysrgb&h=650&w=940";
  return (
    <Link
      to={`/property/${p.id}`}
      onClick={() => fireEvent("ai_search_listing_clicked", { property_id: p.id })}
      data-testid="chat-listing-card"
      className="block rounded-xl overflow-hidden border border-brand-subtle bg-white hover:border-[#C9A24A] transition-colors"
    >
      <div className="flex gap-3">
        <img src={img} alt={p.title} className="w-28 h-28 object-cover shrink-0" />
        <div className="py-2.5 pr-3 min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="font-display text-lg font-semibold brand-forest">{formatPrice(p.price, isRent)}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{p.property_type}</span>
          </div>
          <div className="flex items-center gap-1 text-sm font-semibold text-gray-900 mt-0.5">
            <span className="line-clamp-1">{p.bhk} BHK · {p.locality}</span>
            {p.owner_verified && <BadgeCheck size={14} className="text-emerald-600 shrink-0" />}
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
            <MapPin size={11} /> <span className="line-clamp-1">{p.city}</span>
          </div>
          <div className="flex flex-wrap gap-1 mt-1.5">
            {highlightsFor(p).map((t) => (
              <span key={t} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold border border-emerald-100">
                <Check size={9} /> {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
}

function TypingDots() {
  return (
    <span className="flex gap-1 py-1" data-testid="assistant-typing">
      {[0, 1, 2].map((i) => (
        <span key={i} className="w-1.5 h-1.5 rounded-full bg-[#C9A24A] animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
      ))}
    </span>
  );
}

export default function AssistantPage() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);     // {role:'bot'|'user', text?, results?, alertFor?}
  const [stage, setStage] = useState("listing_type"); // listing_type|property_type|city|bhk|budget|results|refine
  const [answers, setAnswers] = useState({});
  const [cities, setCities] = useState([]);
  const [cityText, setCityText] = useState("");
  const [freeText, setFreeText] = useState("");
  const [typing, setTyping] = useState(false);
  const [email, setEmail] = useState("");
  const [alertBusy, setAlertBusy] = useState(false);
  const [alertDone, setAlertDone] = useState(false);
  const endRef = useRef(null);
  const startedRef = useRef(false);

  const pushBot = (text, extra = {}) => setMessages((m) => [...m, { role: "bot", text, ...extra }]);
  const pushUser = (text) => setMessages((m) => [...m, { role: "user", text }]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typing]);

  // Fetch city chips + greet once.
  useEffect(() => {
    api.get("/cities/counts").then(({ data }) => {
      const sorted = Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([c]) => cap(c));
      setCities(sorted);
    }).catch(() => {});
    if (startedRef.current) return;
    startedRef.current = true;
    const q = params.get("q");
    if (q) { runFreeText(q); }
    else { pushBot("Hi! I'll help you find the right home. First — are you looking to rent or to buy?"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const advance = async (next, answersNow) => {
    setTyping(true);
    await new Promise((r) => setTimeout(r, 450));
    setTyping(false);
    if (next === "property_type") pushBot("Great. What kind of place are you after?");
    else if (next === "city") pushBot("Which city are you looking in?");
    else if (next === "bhk") pushBot("How many bedrooms do you need?");
    else if (next === "budget") pushBot("And what's your budget?");
    setStage(next);
  };

  const runStructured = async (a) => {
    setStage("results");
    setTyping(true);
    fireEvent("ai_search_started", { mode: "guided", ...a });
    try {
      const { data } = await api.post("/ai-search/structured", {
        listing_type: a.listing_type || null,
        city: a.city || null,
        property_type: a.property_type || null,
        bhk: a.bhk ?? null,
        min_price: a.min_price ?? null,
        max_price: a.max_price ?? null,
      });
      setTyping(false);
      pushBot(data.message, {
        results: data.results || [],
        alertFor: (data.match_type === "none" || data.match_type === "nearby") ? data.query : null,
      });
      if (data.match_type === "none" || data.result_count === 0) {
        fireEvent("ai_search_no_results", { ...a, match_type: data.match_type });
      } else {
        fireEvent("ai_search_completed", { result_count: data.result_count, match_type: data.match_type });
      }
      setAlertDone(false);
      setStage("refine");
    } catch (e) {
      setTyping(false);
      pushBot(e?.response?.data?.detail || "I'm having trouble right now — please try the filters on Browse.");
      setStage("refine");
    }
  };

  const runFreeText = async (text) => {
    const msg = (text || "").trim();
    if (!msg) return;
    pushUser(msg);
    setFreeText("");
    setStage("results");
    setTyping(true);
    fireEvent("ai_search_started", { mode: "free", query_text: msg });
    try {
      const { data } = await api.post("/ai-search", { message: msg, context: answers.city ? answers : null });
      setTyping(false);
      const merged = { ...answers, ...(data.query || {}) };
      setAnswers(merged);
      if (data.match_type === "clarify") {
        pushBot(data.message);
      } else {
        pushBot(data.message, {
          results: data.results || [],
          alertFor: (data.match_type === "none" || data.match_type === "nearby") ? data.query : null,
        });
        if (data.match_type === "none" || data.result_count === 0) fireEvent("ai_search_no_results", { ...(data.query || {}), match_type: data.match_type });
        else fireEvent("ai_search_completed", { result_count: data.result_count, match_type: data.match_type });
      }
      setAlertDone(false);
      setStage("refine");
    } catch (e) {
      setTyping(false);
      pushBot(e?.response?.data?.detail || "I'm having trouble right now — please try the filters on Browse.");
      setStage("refine");
    }
  };

  // Quick-reply handlers
  const pick = (stageKey, value, label, patch) => {
    pushUser(label);
    const a = { ...answers, ...patch };
    setAnswers(a);
    if (stageKey === "listing_type") advance("property_type", a);
    else if (stageKey === "property_type") advance("city", a);
    else if (stageKey === "bhk") advance("budget", a);
    else if (stageKey === "budget") runStructured(a);
  };

  const pickCity = (city) => {
    if (!city.trim()) return;
    pushUser(city);
    const a = { ...answers, city: city.trim() };
    setAnswers(a);
    setCityText("");
    advance("bhk", a);
  };

  const restart = () => {
    setMessages([]);
    setAnswers({});
    setAlertDone(false);
    setStage("listing_type");
    setTimeout(() => pushBot("Sure — let's start fresh. Are you looking to rent or to buy?"), 50);
  };

  const submitAlert = async (q) => {
    const em = user?.email || email.trim();
    if (!em) { toast.error("Enter your email"); return; }
    if (!q?.city && !q?.locality) { toast.error("I need a city to set an alert"); return; }
    setAlertBusy(true);
    try {
      await api.post("/saved-searches", {
        email: em, city: q.city || null, locality: q.locality || null,
        listing_type: q.listing_type || null, property_type: q.property_type || null,
        min_price: q.min_price ?? null, max_price: q.max_price ?? null, bhk: q.bhk ?? null,
      });
      setAlertDone(true);
      fireEvent("ai_search_alert_signup", { city: q.city, bhk: q.bhk });
      toast.success("Alert set — I'll email you when a match goes live");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't set the alert");
    } finally { setAlertBusy(false); }
  };

  const budgets = answers.listing_type === "sale" ? BUY_BUDGETS : RENT_BUDGETS;

  return (
    <div className="max-w-2xl mx-auto w-full flex flex-col h-[calc(100dvh-64px)] px-4 sm:px-6">
      <SEO title="AI Home Assistant — find your rental or home to buy"
        description="Chat with our AI assistant to find verified, owner-listed homes to rent or buy. Zero brokerage."
        path="/assistant" />

      {/* Chat header */}
      <div className="flex items-center gap-3 py-4 shrink-0">
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#7A1E2A] to-[#5C1420] flex items-center justify-center shrink-0">
          <Sparkles size={20} className="text-[#F3E4BC]" />
        </div>
        <div>
          <h1 className="font-display text-xl brand-forest leading-tight">Home Assistant</h1>
          <p className="text-xs text-gray-500">Finds real, owner-listed homes · zero brokerage</p>
        </div>
      </div>

      {/* Messages (own scroll area) */}
      <div className="flex-1 overflow-y-auto space-y-4 pb-4" data-testid="assistant-chat">
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className={`max-w-[85%] ${m.role === "user"
              ? "bg-[#7A1E2A] text-white rounded-2xl rounded-br-sm px-4 py-2.5"
              : "bg-white border border-brand-subtle rounded-2xl rounded-bl-sm px-4 py-3"}`}
              data-testid={m.role === "user" ? "chat-user-msg" : "chat-bot-msg"}>
              {m.text && <p className={`text-sm ${m.role === "user" ? "text-white" : "text-gray-800"}`}>{m.text}</p>}

              {Array.isArray(m.results) && m.results.length > 0 && (
                <div className="mt-3 space-y-2.5" data-testid="chat-results">
                  {m.results.slice(0, 6).map((p) => <ChatListing key={p.id} p={p} />)}
                </div>
              )}

              {m.alertFor && (
                <div className="mt-3 rounded-xl bg-[#FFFBEE] border border-[#EFE3D0] p-3" data-testid="chat-alert">
                  {alertDone ? (
                    <p className="text-xs text-emerald-700 font-medium flex items-center gap-1.5"><BellRing size={14} /> Done — I'll email you when a match is listed.</p>
                  ) : (
                    <>
                      <p className="text-xs text-gray-600 mb-2 flex items-center gap-1.5"><BellRing size={14} className="text-[#C9A24A]" /> Want an email when something matches?</p>
                      <div className="flex gap-2">
                        {!user && (
                          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@email.com"
                            data-testid="chat-alert-email"
                            className="flex-1 rounded-lg px-3 py-2 text-sm border border-brand-subtle bg-white outline-none focus:border-[#C9A24A]" />
                        )}
                        <button onClick={() => submitAlert(m.alertFor)} disabled={alertBusy} data-testid="chat-alert-submit"
                          className="shrink-0 rounded-lg px-4 py-2 text-sm font-semibold text-white bg-[#7A1E2A] hover:bg-[#661620] disabled:opacity-60">
                          {alertBusy ? <Loader2 size={14} className="animate-spin" /> : "Notify me"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        {typing && (
          <div className="flex justify-start">
            <div className="bg-white border border-brand-subtle rounded-2xl rounded-bl-sm px-4 py-2"><TypingDots /></div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Quick replies + input (part of the column, pinned at the bottom) */}
      <div className="shrink-0 pt-3 pb-4 bg-[#FBF6EC]">
        {/* Quick-reply chips per stage */}
        <div className="flex flex-wrap gap-2 mb-2" data-testid="chat-quick-replies">
          {stage === "listing_type" && (
            <>
              <QuickChip onClick={() => pick("listing_type", "rent", "I want to rent", { listing_type: "rent" })}>Rent a home</QuickChip>
              <QuickChip onClick={() => pick("listing_type", "sale", "I want to buy", { listing_type: "sale" })}>Buy a home</QuickChip>
            </>
          )}
          {stage === "property_type" && (
            <>
              <QuickChip onClick={() => pick("property_type", "apartment", "Apartment / Flat", { property_type: "apartment" })}>Apartment / Flat</QuickChip>
              <QuickChip onClick={() => pick("property_type", "house", "Independent house", { property_type: "house" })}>Independent house</QuickChip>
              <QuickChip onClick={() => pick("property_type", "villa", "Villa", { property_type: "villa" })}>Villa</QuickChip>
              <QuickChip onClick={() => pick("property_type", null, "Show me all types", { property_type: null })}>Any type</QuickChip>
            </>
          )}
          {stage === "city" && cities.map((c) => <QuickChip key={c} onClick={() => pickCity(c)}>{c}</QuickChip>)}
          {stage === "bhk" && (
            <>
              {[1, 2, 3].map((n) => <QuickChip key={n} onClick={() => pick("bhk", n, `${n} BHK`, { bhk: n })}>{n} BHK</QuickChip>)}
              <QuickChip onClick={() => pick("bhk", 4, "4+ BHK", { bhk: 4 })}>4+ BHK</QuickChip>
              <QuickChip onClick={() => pick("bhk", null, "Any size", { bhk: null })}>Any</QuickChip>
            </>
          )}
          {stage === "budget" && budgets.map((b) => (
            <QuickChip key={b.label} onClick={() => pick("budget", b.label, b.label, { min_price: b.any ? null : (b.min_price ?? null), max_price: b.any ? null : (b.max_price ?? null) })}>{b.label}</QuickChip>
          ))}
          {stage === "refine" && (
            <QuickChip onClick={restart}><RotateCcw size={13} className="inline mr-1" /> New search</QuickChip>
          )}
        </div>

        {/* City stage also allows typing a city */}
        {stage === "city" && (
          <form onSubmit={(e) => { e.preventDefault(); pickCity(cityText); }} className="flex gap-2 mb-2">
            <input value={cityText} onChange={(e) => setCityText(e.target.value)} placeholder="…or type your city"
              data-testid="chat-city-input"
              className="flex-1 rounded-full px-4 py-2.5 text-sm border border-brand-subtle bg-white outline-none focus:border-[#C9A24A]" />
            <button type="submit" className="rounded-full px-4 bg-[#7A1E2A] text-white"><Send size={16} /></button>
          </form>
        )}

        {/* Free-text — always available */}
        <form onSubmit={(e) => { e.preventDefault(); runFreeText(freeText); }}
          className="flex items-center gap-2 rounded-full pl-4 pr-1.5 py-1.5 bg-white border border-brand-subtle shadow-sm">
          <input value={freeText} onChange={(e) => setFreeText(e.target.value)}
            data-testid="assistant-input"
            placeholder="Or just type — “2BHK in Gurgaon under 25k”"
            className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400 min-w-0" />
          <button type="submit" data-testid="assistant-submit" className="shrink-0 w-9 h-9 rounded-full bg-[#7A1E2A] text-white flex items-center justify-center hover:bg-[#661620]">
            <Send size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}

function QuickChip({ children, onClick }) {
  return (
    <button onClick={onClick} data-testid="quick-reply-chip"
      className="px-3.5 py-2 rounded-full text-sm font-medium bg-white border border-[#C9A24A]/50 text-[#7A1E2A] hover:bg-[#7A1E2A] hover:text-white transition-colors">
      {children}
    </button>
  );
}
